import { defaultAppUserId, supabase } from '../config/supabase';
import { lookupLabel } from '../data/lookups';
import type { LookupState, TimeFormat, UserPreferences } from '../types';
import { formatPlayerName } from './playerPreferences';
import { loadParticipationReportRows } from './participationReport';
import { getNotificationSetting, loadNotificationSettingsByWorkspace, type NotificationSettingsByWorkspace } from './notificationSettings';
import { formatTime } from './timeFormat';

export type NotificationCategory = 'Action Required' | 'Alerts & Reminders' | 'Outstanding Fees' | 'Informational';
export type NotificationSeverity = 'Critical' | 'Warning' | 'Informational';
export type NotificationActionKind = 'route' | 'copy_welcome' | 'mark_registered' | 'run_tennisrecord_sync';

export type NotificationAction = {
  label: string;
  route?: string;
  kind?: NotificationActionKind;
};

export type CommandCenterNotification = {
  key: string;
  notificationType: string;
  category: NotificationCategory;
  severity: NotificationSeverity;
  title: string;
  message: string;
  teamId?: string | null;
  teamName?: string;
  playerId?: string | null;
  playerName?: string;
  matchEventId?: string | null;
  opposingCaptainNames?: string | null;
  rosterId?: string | null;
  dueDate?: string | null;
  sortDate: string;
  dismissible: boolean;
  primaryAction?: NotificationAction;
  secondaryAction?: NotificationAction;
};

type EngineOptions = {
  lookups: LookupState;
  timeFormat: TimeFormat;
  userPreferences: UserPreferences;
  writableTeamIds?: string[];
  isOwner?: boolean;
};

type MatchRow = {
  id: string;
  team_id: string;
  season_id?: string | null;
  league_configuration_id: string;
  opponent_name: string | null;
  match_date: string;
  match_time: string | null;
  match_site_type: string | null;
  opponent_captain_names?: string | null;
  match_status: string;
  lineup_status: string | null;
  match_results?: { result_status: string | null }[] | null;
};

type ScoreDueMatchRow = MatchRow & { line_results?: { id: string }[] | null };

type PositionRow = {
  id: string;
  league_configuration_id: string;
  position_code: string;
  line_type: string | null;
  required_player_count: number | null;
  is_doubles: boolean | null;
};

type EntryRow = {
  match_event_id: string;
  league_configuration_position_id: string;
  player_id: string;
};

type AvailabilityRow = {
  match_event_id: string;
  player_id: string;
  availability_status: string;
};

type RosterRow = {
  team_id: string;
  player_id: string;
};

type PlayerRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  birthday?: string | null;
};

type TeamRow = {
  id: string;
  season_id: string | null;
  seasons?: { start_date: string | null } | null;
};

type BirthdayRosterRow = {
  player_id: string;
  players?: PlayerRow | null;
  teams?: { id: string; team_name: string; is_active: boolean } | null;
};

type RegistrationRosterRow = {
  id: string;
  registration_status: string | null;
  players?: PlayerRow | null;
  teams?: {
    id: string;
    team_name: string;
    is_active: boolean;
    seasons?: {
      start_date: string | null;
      end_date: string | null;
    } | null;
  } | null;
};

type StoredAvailabilityNotificationRow = {
  notification_key: string;
  notification_type: string;
  category: NotificationCategory;
  severity: NotificationSeverity;
  title: string;
  message: string;
  team_id: string;
  player_id: string;
  match_event_id: string;
  due_date: string | null;
  created_at: string;
};

type StoredPaymentNotificationRow = {
  notification_key: string;
  notification_type: string;
  category: NotificationCategory;
  severity: NotificationSeverity;
  title: string;
  message: string;
  team_id: string | null;
  player_id: string | null;
  due_date: string | null;
  created_at: string;
  action_label: string | null;
  action_route: string | null;
};

const storedAvailabilityNotificationTypes = [
  'assigned_player_availability_changed',
  'groupme_assigned_player_available',
  'groupme_assigned_player_maybe',
  'groupme_assigned_player_unavailable',
];

const storedPaymentNotificationTypes = [
  'payment_email_needs_review',
];

const groupMeAvailabilityStatusByType: Record<string, string> = {
  groupme_assigned_player_available: 'available',
  groupme_assigned_player_maybe: 'maybe',
  groupme_assigned_player_unavailable: 'not_available',
};

function activeLookupTeamIds(lookups: LookupState): string[] {
  return lookups.teams
    .filter((team) => team.is_active !== false)
    .map((team) => team.value);
}

function activeLookupTeamIdSet(lookups: LookupState): Set<string> {
  return new Set(activeLookupTeamIds(lookups));
}

function availabilityNotificationSettingKey(notificationType: string): string {
  return storedAvailabilityNotificationTypes.includes(notificationType)
    ? 'assigned_player_availability_changed'
    : notificationType;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(dateIso: string, days: number): string {
  const date = new Date(`${dateIso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00`).getTime();
  const to = new Date(`${toIso}T00:00:00`).getTime();
  return Math.round((to - from) / 86_400_000);
}

function hoursBetween(fromIso: string, toIso: string): number {
  return Math.floor((new Date(toIso).getTime() - new Date(fromIso).getTime()) / 3_600_000);
}

function formatDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function urgencyText(matchDate: string): string {
  const delta = daysBetween(todayIso(), matchDate);
  if (delta === 0) return 'Today';
  if (delta > 0) return `Match in ${delta} day${delta === 1 ? '' : 's'}`;
  const elapsed = Math.abs(delta);
  return `Match was ${elapsed} day${elapsed === 1 ? '' : 's'} ago`;
}

function resultStatus(match: MatchRow): string {
  return match.match_results?.[0]?.result_status || 'Not Started';
}

function birthdayOccurrenceThisYear(birthday: string, baseYear: number): string {
  const [, month, day] = birthday.split('-');
  return `${baseYear}-${month}-${day}`;
}

function matchLabel(match: MatchRow, timeFormat: TimeFormat): string {
  const time = formatTime(match.match_time, timeFormat);
  return `vs ${match.opponent_name || 'TBD'} • ${formatDate(match.match_date)}${time ? ` ${time}` : ''}`;
}

function isTcdScoresheetConfiguration(positions: PositionRow[]): boolean {
  if (positions.length !== 4) return false;
  return positions.every((position) => (
    position.is_doubles === true
    && Number(position.required_player_count || 0) === 2
    && ['doubles', 'mixed_doubles'].includes(position.line_type || '')
  ));
}

function severityRank(severity: NotificationSeverity): number {
  if (severity === 'Critical') return 0;
  if (severity === 'Warning') return 1;
  return 2;
}

async function loadDismissedKeys(): Promise<Set<string>> {
  const keys = new Set<string>();
  const dismissalResult = await supabase.from('notification_dismissals').select('notification_key');
  if (!dismissalResult.error) {
    ((dismissalResult.data || []) as { notification_key: string }[]).forEach((row) => keys.add(row.notification_key));
  }

  const legacyResult = await supabase.from('command_center_reminders').select('reminder_type, match_event_id, dismissed').eq('dismissed', true).is('deleted_at', null);
  if (!legacyResult.error) {
    ((legacyResult.data || []) as { reminder_type: string; match_event_id: string }[]).forEach((row) => keys.add(`${row.reminder_type}:${row.match_event_id}`));
  }
  return keys;
}

async function persistInstances(notifications: CommandCenterNotification[]): Promise<void> {
  const generatedNotifications = notifications.filter(
    (notification) => (
      !storedAvailabilityNotificationTypes.includes(notification.notificationType)
      && !storedPaymentNotificationTypes.includes(notification.notificationType)
    ),
  );
  if (generatedNotifications.length === 0) return;
  const rows = generatedNotifications.map((notification) => ({
    notification_key: notification.key,
    notification_type: notification.notificationType,
    category: notification.category,
    severity: notification.severity,
    title: notification.title,
    message: notification.message,
    team_id: notification.teamId || null,
    player_id: notification.playerId || null,
    match_event_id: notification.matchEventId || null,
    due_date: notification.dueDate || null,
    action_label: notification.primaryAction?.label || null,
    action_route: notification.primaryAction?.route || notification.primaryAction?.kind || null,
    secondary_action_label: notification.secondaryAction?.label || null,
    secondary_action_route: notification.secondaryAction?.route || notification.secondaryAction?.kind || null,
    resolved: false,
    updated_at: new Date().toISOString(),
  }));
  await supabase.from('notification_instances').upsert(rows, { onConflict: 'notification_key' });
}

async function loadStoredPaymentNotifications(
  dismissedKeys: Set<string>,
  lookups: LookupState,
  settingsByWorkspace: NotificationSettingsByWorkspace,
): Promise<CommandCenterNotification[]> {
  const { data, error } = await supabase
    .from('notification_instances')
    .select('notification_key, notification_type, category, severity, title, message, team_id, player_id, due_date, created_at, action_label, action_route')
    .in('notification_type', storedPaymentNotificationTypes)
    .eq('resolved', false)
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) {
    if (error.code === '42P01' || error.message.includes('payment_email')) return [];
    throw new Error(error.message);
  }

  return ((data || []) as unknown as StoredPaymentNotificationRow[])
    .filter((row) => {
      if (row.team_id && lookups.teams.find((team) => team.value === row.team_id)?.is_active === false) return false;
      if (dismissedKeys.has(row.notification_key)) return false;
      const workspaceId = row.team_id ? lookups.teams.find((team) => team.value === row.team_id)?.workspace_id : undefined;
      return getNotificationSetting(settingsByWorkspace, workspaceId, row.notification_type).enabled;
    })
    .map((row) => ({
      key: row.notification_key,
      notificationType: row.notification_type,
      category: row.category,
      severity: row.severity,
      title: row.title,
      message: row.message,
      teamId: row.team_id,
      teamName: row.team_id ? lookupLabel(lookups, 'teams', row.team_id) : undefined,
      playerId: row.player_id,
      playerName: row.player_id ? lookupLabel(lookups, 'players', row.player_id) : undefined,
      dueDate: row.due_date,
      sortDate: row.created_at,
      dismissible: false,
      primaryAction: { label: row.action_label || 'Open Payment Inbox', route: row.action_route || 'payment-inbox' },
    }));
}

async function loadStoredAvailabilityNotifications(
  dismissedKeys: Set<string>,
  lookups: LookupState,
  writableTeamIds: string[],
  isOwner: boolean,
  settingsByWorkspace: NotificationSettingsByWorkspace,
): Promise<CommandCenterNotification[]> {
  const activeTeamIds = activeLookupTeamIdSet(lookups);
  const { data, error } = await supabase
    .from('notification_instances')
    .select('notification_key, notification_type, category, severity, title, message, team_id, player_id, match_event_id, due_date, created_at')
    .in('notification_type', storedAvailabilityNotificationTypes)
    .eq('resolved', false)
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) {
    if (error.code === '42P01' || error.message.includes('availability_change')) return [];
    throw new Error(error.message);
  }

  const rows = (data || []) as unknown as StoredAvailabilityNotificationRow[];
  const matchIds = [...new Set(rows.map((row) => row.match_event_id).filter(Boolean))];
  const playerIds = [...new Set(rows.map((row) => row.player_id).filter(Boolean))];
  const [entryResult, availabilityResult] = await Promise.all([
    matchIds.length && playerIds.length
      ? supabase.from('lineup_entries').select('match_event_id, player_id').in('match_event_id', matchIds).in('player_id', playerIds).is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
    matchIds.length && playerIds.length
      ? supabase.from('availability_records').select('match_event_id, player_id, availability_status').in('match_event_id', matchIds).in('player_id', playerIds).is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (entryResult.error || availabilityResult.error) {
    throw new Error(entryResult.error?.message || availabilityResult.error?.message || 'Unable to validate GroupMe availability notifications.');
  }

  const assignedPlayerKeys = new Set(
    ((entryResult.data || []) as Array<{ match_event_id: string; player_id: string }>).map((entry) => `${entry.match_event_id}:${entry.player_id}`),
  );
  const availabilityByPlayer = new Map(
    ((availabilityResult.data || []) as AvailabilityRow[]).map((row) => [`${row.match_event_id}:${row.player_id}`, row.availability_status]),
  );
  const staleKeys = rows
    .filter((row) => {
      const key = `${row.match_event_id}:${row.player_id}`;
      const expectedStatus = groupMeAvailabilityStatusByType[row.notification_type];
      return !assignedPlayerKeys.has(key) || (expectedStatus ? availabilityByPlayer.get(key) !== expectedStatus : false);
    })
    .map((row) => row.notification_key);
  if (staleKeys.length > 0) {
    await supabase
      .from('notification_instances')
      .update({ resolved: true, updated_at: new Date().toISOString() })
      .in('notification_key', staleKeys);
  }

  const writableTeams = new Set(writableTeamIds);
  return rows
    .filter((row) => !staleKeys.includes(row.notification_key))
    .filter((row) => activeTeamIds.has(row.team_id))
    .filter((row) => {
      const settingKey = availabilityNotificationSettingKey(row.notification_type);
      const setting = getNotificationSetting(settingsByWorkspace, lookups.teams.find((team) => team.value === row.team_id)?.workspace_id, settingKey);
      return setting.enabled && !dismissedKeys.has(row.notification_key);
    })
    .map((row) => {
      const canEditLineup = isOwner || writableTeams.has(row.team_id);
      const becameAvailable = row.notification_type === 'groupme_assigned_player_available' || row.severity === 'Informational';
      return {
        key: row.notification_key,
        notificationType: row.notification_type,
        category: row.category,
        severity: row.severity,
        title: row.title,
        message: row.message,
        teamId: row.team_id,
        teamName: lookupLabel(lookups, 'teams', row.team_id),
        playerId: row.player_id,
        playerName: lookupLabel(lookups, 'players', row.player_id),
        matchEventId: row.match_event_id,
        dueDate: row.due_date,
        sortDate: row.created_at,
        dismissible: true,
        primaryAction: becameAvailable || !canEditLineup
          ? { label: 'Open Availability', route: `availability:${row.team_id}` }
          : { label: 'Edit Lineup', route: `lineup:${row.match_event_id}` },
        secondaryAction: canEditLineup
          ? becameAvailable
            ? { label: 'Open Lineup', route: `lineup:${row.match_event_id}` }
            : { label: 'Open Availability', route: `availability:${row.team_id}` }
          : undefined,
      };
    });
}

export async function dismissCommandCenterNotification(notification: CommandCenterNotification, userId = defaultAppUserId): Promise<void> {
  const payload = {
    notification_key: notification.key,
    notification_type: notification.notificationType,
    dismissed_at: new Date().toISOString(),
    dismissed_by: userId,
  };
  const { error } = await supabase.from('notification_dismissals').upsert(payload, { onConflict: 'notification_key,dismissed_by' });
  if (!error) return;

  if (error.message.includes('no unique or exclusion constraint matching the ON CONFLICT specification')) {
    const fallback = await supabase.from('notification_dismissals').insert(payload);
    if (!fallback.error || fallback.error.code === '23505') return;
    throw new Error(fallback.error.message);
  }

  throw new Error(error.message);
}

export async function loadCommandCenterNotifications({
  lookups,
  timeFormat,
  userPreferences,
  writableTeamIds = [],
  isOwner = false,
}: EngineOptions): Promise<CommandCenterNotification[]> {
  const today = todayIso();
  const earliestResultAlertDate = addDays(today, -21);
  const latestUpcomingDate = addDays(today, 30);
  const dismissedKeys = await loadDismissedKeys();
  const notifications: CommandCenterNotification[] = [];
  const activeTeamIds = activeLookupTeamIds(lookups);
  const activeTeamIdSet = new Set(activeTeamIds);
  const activeLookups = { ...lookups, teams: lookups.teams.filter((team) => activeTeamIdSet.has(team.value)) };
  const workspaceIdByTeamId = new Map(activeLookups.teams.map((team) => [team.value, team.workspace_id || '']));
  const settingsByWorkspace = await loadNotificationSettingsByWorkspace([
    ...lookups.workspaces.map((workspace) => workspace.value),
    ...activeLookups.teams.map((team) => team.workspace_id || '').filter(Boolean),
  ]);
  const availabilityNotifications = await loadStoredAvailabilityNotifications(
    dismissedKeys,
    activeLookups,
    writableTeamIds.filter((teamId) => activeTeamIdSet.has(teamId)),
    isOwner,
    settingsByWorkspace,
  );
  notifications.push(...availabilityNotifications);
  const paymentNotifications = await loadStoredPaymentNotifications(dismissedKeys, activeLookups, settingsByWorkspace);
  notifications.push(...paymentNotifications);
  if (activeTeamIds.length === 0) {
    return [];
  }

  const { data: matchData, error: matchError } = await supabase
    .from('match_events')
    .select('id, team_id, season_id, league_configuration_id, opponent_name, opponent_captain_names, match_date, match_time, match_site_type, match_status, lineup_status, match_results(result_status)')
    .is('deleted_at', null)
    .in('team_id', activeTeamIds)
    .gte('match_date', earliestResultAlertDate)
    .lte('match_date', latestUpcomingDate)
    .neq('match_status', 'Cancelled')
    .order('match_date', { ascending: true })
    .order('match_time', { ascending: true })
    .limit(120);
  if (matchError) throw new Error(matchError.message);

  const matches = (matchData || []) as unknown as MatchRow[];
  const scoreDueResult = await supabase
    .from('match_events')
    .select('id, team_id, season_id, league_configuration_id, opponent_name, opponent_captain_names, match_date, match_time, match_site_type, match_status, lineup_status, match_results(result_status), line_results(id)')
    .is('deleted_at', null)
    .in('team_id', activeTeamIds)
    .lte('match_date', today)
    .neq('match_status', 'Cancelled')
    .order('match_date', { ascending: false })
    .limit(120);
  if (scoreDueResult.error) throw new Error(scoreDueResult.error.message);
  const scoreDueMatches = (scoreDueResult.data || []) as unknown as ScoreDueMatchRow[];
  const scoreOccurrenceResult = scoreDueMatches.length > 0
    ? await supabase.rpc('match_occurrence_states', { p_match_ids: scoreDueMatches.map((match) => match.id) })
    : { data: [], error: null };
  if (scoreOccurrenceResult.error) throw new Error(scoreOccurrenceResult.error.message);
  const scoreOccurrenceById = new Map(((scoreOccurrenceResult.data || []) as Array<{
    match_event_id: string;
    occurrence_state: string;
    scheduled_end_at: string | null;
    result_completeness: string;
  }>).map((row) => [row.match_event_id, row]));
  const upcomingMatches = matches.filter((match) => match.match_date >= today && match.match_status !== 'Completed');
  const matchIds = matches.map((match) => match.id);
  const configIds = [...new Set(matches.map((match) => match.league_configuration_id))];
  const teamIds = [...new Set(matches.map((match) => match.team_id))];

  const [positionsResult, entriesResult, availabilityResult, rosterResult] = await Promise.all([
    configIds.length
      ? supabase.from('league_configuration_positions').select('id, league_configuration_id, position_code, line_type, required_player_count, is_doubles').in('league_configuration_id', configIds).is('deleted_at', null).eq('is_active', true)
      : Promise.resolve({ data: [], error: null }),
    matchIds.length
      ? supabase.from('lineup_entries').select('match_event_id, league_configuration_position_id, player_id').in('match_event_id', matchIds).is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
    matchIds.length
      ? supabase.from('availability_records').select('match_event_id, player_id, availability_status').in('match_event_id', matchIds).is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
    teamIds.length
      ? supabase.from('team_rosters').select('team_id, player_id').in('team_id', teamIds).eq('is_active', true).neq('roster_role', 'designated_substitute').is('deleted_at', null)
      : Promise.resolve({ data: [], error: null }),
  ]);
  if (positionsResult.error || entriesResult.error || availabilityResult.error || rosterResult.error) {
    throw new Error(positionsResult.error?.message || entriesResult.error?.message || availabilityResult.error?.message || rosterResult.error?.message || 'Unable to load notifications.');
  }

  const positions = (positionsResult.data || []) as unknown as PositionRow[];
  const entries = (entriesResult.data || []) as unknown as EntryRow[];
  const availabilityRows = (availabilityResult.data || []) as unknown as AvailabilityRow[];
  const rosterRows = (rosterResult.data || []) as unknown as RosterRow[];
  const positionById = new Map(positions.map((position) => [position.id, position]));
  const availabilityByPlayer = new Map(availabilityRows.map((row) => [`${row.match_event_id}:${row.player_id}`, row.availability_status]));
  const playerIds = [...new Set([...entries.map((entry) => entry.player_id), ...rosterRows.map((row) => row.player_id)])];
  const playersResult = playerIds.length ? await supabase.from('players').select('id, first_name, last_name').in('id', playerIds).is('deleted_at', null).eq('is_active', true) : { data: [], error: null };
  if (playersResult.error) throw new Error(playersResult.error.message);
  const playerById = new Map(((playersResult.data || []) as unknown as PlayerRow[]).map((player) => [player.id, player]));
  const activePlayerIds = new Set(playerById.keys());
  const rosterPlayersByTeam = rosterRows.reduce<Record<string, Set<string>>>((acc, row) => {
    if (!activePlayerIds.has(row.player_id)) return acc;
    acc[row.team_id] ||= new Set<string>();
    acc[row.team_id].add(row.player_id);
    return acc;
  }, {});

  upcomingMatches.forEach((match) => {
    const daysUntilMatch = daysBetween(today, match.match_date);
    const teamName = lookupLabel(activeLookups, 'teams', match.team_id);
    const workspaceId = workspaceIdByTeamId.get(match.team_id);
    const lineupNotStarted = getNotificationSetting(settingsByWorkspace, workspaceId, 'lineup_not_started');
    const lineupNotPublished = getNotificationSetting(settingsByWorkspace, workspaceId, 'lineup_not_published');
    const homeWelcome = getNotificationSetting(settingsByWorkspace, workspaceId, 'home_match_welcome_message');
    const tcdScoresheetPrint = getNotificationSetting(settingsByWorkspace, workspaceId, 'tcd_scoresheet_print_reminder');
    const awayFollowUp = getNotificationSetting(settingsByWorkspace, workspaceId, 'away_match_follow_up');
    if (lineupNotStarted.enabled && daysUntilMatch <= Number(lineupNotStarted.thresholdValue) && match.lineup_status === 'Not Started') {
      notifications.push({
        key: `lineup_not_started:${match.id}`,
        notificationType: 'lineup_not_started',
        category: 'Action Required',
        severity: lineupNotStarted.severity,
        title: 'Lineup Not Started',
        message: `${teamName} ${matchLabel(match, timeFormat)} • ${urgencyText(match.match_date)}`,
        teamId: match.team_id,
        teamName,
        matchEventId: match.id,
        dueDate: match.match_date,
        sortDate: match.match_date,
        dismissible: false,
        primaryAction: { label: 'Open Match Lineup', route: `lineup:${match.id}` },
      });
    }
    if (lineupNotPublished.enabled && daysUntilMatch <= Number(lineupNotPublished.thresholdValue) && match.lineup_status !== 'Published') {
      notifications.push({
        key: `lineup_not_published:${match.id}`,
        notificationType: 'lineup_not_published',
        category: 'Action Required',
        severity: lineupNotPublished.severity,
        title: 'Lineup Not Published',
        message: `${teamName} ${matchLabel(match, timeFormat)} • ${urgencyText(match.match_date)}`,
        teamId: match.team_id,
        teamName,
        matchEventId: match.id,
        dueDate: match.match_date,
        sortDate: match.match_date,
        dismissible: false,
        primaryAction: { label: 'Open Match Lineup', route: `lineup:${match.id}` },
      });
    }
    if (homeWelcome.enabled && match.match_site_type === 'Home' && daysUntilMatch <= Number(homeWelcome.thresholdValue)) {
      notifications.push({
        key: `home_match_welcome_message:${match.id}`,
        notificationType: 'home_match_welcome_message',
        category: 'Alerts & Reminders',
        severity: homeWelcome.severity,
        title: 'Send Captain Welcome Message',
        message: `${teamName} ${matchLabel(match, timeFormat)} • Home Match • ${urgencyText(match.match_date)}. You typically send a welcome message to the opposing captain about one week before home matches.`,
        teamId: match.team_id,
        teamName,
        matchEventId: match.id,
        opposingCaptainNames: match.opponent_captain_names || null,
        dueDate: match.match_date,
        sortDate: match.match_date,
        dismissible: true,
        primaryAction: { label: 'Copy Welcome Message', kind: 'copy_welcome' },
      });
    }
    if (
      tcdScoresheetPrint.enabled
      && daysUntilMatch <= Number(tcdScoresheetPrint.thresholdValue)
      && isTcdScoresheetConfiguration(positions.filter((position) => position.league_configuration_id === match.league_configuration_id))
    ) {
      notifications.push({
        key: `tcd_scoresheet_print_reminder:${match.id}`,
        notificationType: 'tcd_scoresheet_print_reminder',
        category: 'Alerts & Reminders',
        severity: tcdScoresheetPrint.severity,
        title: 'Print TCD Scoresheet',
        message: `${teamName} ${matchLabel(match, timeFormat)} • ${urgencyText(match.match_date)}. Print the TCD scoresheet before match time.`,
        teamId: match.team_id,
        teamName,
        matchEventId: match.id,
        dueDate: match.match_date,
        sortDate: match.match_date,
        dismissible: true,
        primaryAction: { label: 'Print Scoresheet', route: `lineup:${match.id}` },
      });
    }
    if (awayFollowUp.enabled && match.match_site_type === 'Away' && daysUntilMatch <= Number(awayFollowUp.thresholdValue)) {
      notifications.push({
        key: `away_match_follow_up:${match.id}`,
        notificationType: 'away_match_follow_up',
        category: 'Alerts & Reminders',
        severity: awayFollowUp.severity,
        title: 'Away Match Follow-Up',
        message: `${teamName} ${matchLabel(match, timeFormat)} • Away Match • ${urgencyText(match.match_date)}. Have you heard from the opposing captain yet?`,
        teamId: match.team_id,
        teamName,
        matchEventId: match.id,
        dueDate: match.match_date,
        sortDate: match.match_date,
        dismissible: true,
      });
    }
  });


  upcomingMatches.forEach((match) => {
    const missingAvailability = getNotificationSetting(settingsByWorkspace, workspaceIdByTeamId.get(match.team_id), 'missing_availability');
    if (!missingAvailability.enabled) return;
    const daysUntilMatch = daysBetween(today, match.match_date);
    if (daysUntilMatch > Number(missingAvailability.thresholdValue)) return;
    const rosterPlayerIds = rosterPlayersByTeam[match.team_id] || new Set<string>();
    const unknownCount = Array.from(rosterPlayerIds).filter((playerId) => (availabilityByPlayer.get(`${match.id}:${playerId}`) || 'unknown') === 'unknown').length;
    if (unknownCount === 0) return;
    const teamName = lookupLabel(activeLookups, 'teams', match.team_id);
    notifications.push({
      key: `missing_availability:${match.id}`,
      notificationType: 'missing_availability',
      category: 'Alerts & Reminders',
      severity: missingAvailability.severity,
      title: 'Missing Availability',
      message: `${teamName} ${matchLabel(match, timeFormat)} • ${unknownCount} player${unknownCount === 1 ? '' : 's'} still have unknown availability.`,
      teamId: match.team_id,
      teamName,
      matchEventId: match.id,
      dueDate: match.match_date,
      sortDate: match.match_date,
      dismissible: true,
      primaryAction: { label: 'Open Availability', route: `availability:${match.team_id}` },
    });
  });

  scoreDueMatches
    .filter((match) => {
      const setting = getNotificationSetting(settingsByWorkspace, workspaceIdByTeamId.get(match.team_id), 'match_scores_required');
      const occurrence = scoreOccurrenceById.get(match.id);
      return setting.enabled && occurrence?.occurrence_state === 'Past'
        && occurrence.result_completeness === 'Awaiting Scores'
        && occurrence.scheduled_end_at != null
        && hoursBetween(occurrence.scheduled_end_at, new Date().toISOString()) >= Number(setting.thresholdValue);
    })
    .forEach((match) => {
      const setting = getNotificationSetting(settingsByWorkspace, workspaceIdByTeamId.get(match.team_id), 'match_scores_required');
      notifications.push({
        key: `match_scores_required:${match.id}`,
        notificationType: 'match_scores_required',
        category: 'Action Required',
        severity: setting.severity,
        title: 'Enter Match Scores',
        message: `${lookupLabel(activeLookups, 'teams', match.team_id)} vs ${match.opponent_name || 'TBD'} • ${urgencyText(match.match_date)} • No match scores have been captured.`,
        teamId: match.team_id,
        teamName: lookupLabel(activeLookups, 'teams', match.team_id),
        matchEventId: match.id,
        dueDate: match.match_date,
        sortDate: match.match_date,
        dismissible: true,
        primaryAction: { label: 'Enter Scores', route: `scores:${match.id}` },
      });
    });

  scoreDueMatches
    .filter((match) => {
      const setting = getNotificationSetting(settingsByWorkspace, workspaceIdByTeamId.get(match.team_id), 'match_scores_partial');
      return setting.enabled
        && scoreOccurrenceById.get(match.id)?.occurrence_state === 'Past'
        && scoreOccurrenceById.get(match.id)?.result_completeness === 'Partial Results'
        && resultStatus(match) === 'Draft';
    })
    .forEach((match) => {
      const setting = getNotificationSetting(settingsByWorkspace, workspaceIdByTeamId.get(match.team_id), 'match_scores_partial');
      notifications.push({
        key: `match_scores_partial:${match.id}`,
        notificationType: 'match_scores_partial',
        category: 'Action Required',
        severity: setting.severity,
        title: 'Finish Match Scores',
        message: `Scores partially entered for ${match.opponent_name || 'TBD'}. Finish score entry.`,
        teamId: match.team_id,
        teamName: lookupLabel(activeLookups, 'teams', match.team_id),
        matchEventId: match.id,
        dueDate: match.match_date,
        sortDate: match.match_date,
        dismissible: true,
        primaryAction: { label: 'Open Score Entry', route: `scores:${match.id}` },
      });
    });

  matches
    .filter((match) => match.match_status === 'Scores Entered' && resultStatus(match) === 'Confirmed')
    .forEach((match) => {
      notifications.push({
        key: `results_confirmed_not_submitted:${match.id}`,
        notificationType: 'results_confirmed_not_submitted',
        category: 'Action Required',
        severity: 'Warning',
        title: 'Results Confirmed but Not Submitted',
        message: `${lookupLabel(activeLookups, 'teams', match.team_id)} vs ${match.opponent_name || 'TBD'} • Results confirmed but not submitted`,
        teamId: match.team_id,
        teamName: lookupLabel(activeLookups, 'teams', match.team_id),
        matchEventId: match.id,
        dueDate: match.match_date,
        sortDate: match.match_date,
        dismissible: false,
        primaryAction: { label: 'Open Match Results', route: `match:${match.id}` },
      });
    });

  await appendParticipationNotifications(notifications, activeLookups, userPreferences, today, settingsByWorkspace, workspaceIdByTeamId);
  await appendTennisRecordEdrSyncNotification(notifications, lookups, today, settingsByWorkspace);
  await appendBirthdayNotifications(notifications, activeLookups, userPreferences, today, settingsByWorkspace, workspaceIdByTeamId);
  await appendRegistrationNotifications(notifications, activeLookups, userPreferences, today, settingsByWorkspace, workspaceIdByTeamId);

  const visibleNotifications = Array.from(
    notifications
      .filter((notification) => !notification.dismissible || !dismissedKeys.has(notification.key))
      .reduce((itemsByKey, notification) => {
        if (!itemsByKey.has(notification.key)) itemsByKey.set(notification.key, notification);
        return itemsByKey;
      }, new Map<string, CommandCenterNotification>())
      .values(),
  )
    .sort((a, b) => severityRank(a.severity) - severityRank(b.severity) || a.sortDate.localeCompare(b.sortDate) || a.title.localeCompare(b.title));
  await persistInstances(visibleNotifications);
  return visibleNotifications;
}

async function appendTennisRecordEdrSyncNotification(
  notifications: CommandCenterNotification[],
  lookups: LookupState,
  today: string,
  settingsByWorkspace: NotificationSettingsByWorkspace,
): Promise<void> {
  const workspace = lookups.workspaces[0];
  if (!workspace) return;
  const setting = getNotificationSetting(settingsByWorkspace, workspace.value, 'tennisrecord_edr_sync_due');
  if (!setting.enabled) return;
  const weeks = Math.max(1, Number(setting.thresholdValue) || 1);
  const cutoff = new Date(Date.now() - weeks * 7 * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('players')
    .select('id, tennis_record_last_successful_sync_at')
    .eq('is_active', true)
    .is('deleted_at', null)
    .not('tennis_record_profile_url', 'is', null)
    .limit(1000);
  if (error) return;
  const rows = (data || []) as { id: string; tennis_record_last_successful_sync_at: string | null }[];
  const dueCount = rows.filter((player) => !player.tennis_record_last_successful_sync_at || player.tennis_record_last_successful_sync_at < cutoff).length;
  if (dueCount === 0) return;
  notifications.push({
    key: `tennisrecord_edr_sync_due:${workspace.value}:${weeks}`,
    notificationType: 'tennisrecord_edr_sync_due',
    category: 'Alerts & Reminders',
    severity: setting.severity,
    title: 'TennisRecord EDR Sync Due',
    message: `${dueCount} active player${dueCount === 1 ? '' : 's'} ${dueCount === 1 ? 'has' : 'have'} not had EDR refreshed in ${weeks}+ week${weeks === 1 ? '' : 's'}. Start the browser-based serial sync and keep the tab open while it runs.`,
    dueDate: today,
    sortDate: today,
    dismissible: true,
    primaryAction: { label: 'Sync TennisRecord', kind: 'run_tennisrecord_sync' },
    secondaryAction: { label: 'Open Settings', route: 'tennisrecord-sync' },
  });
}

async function appendParticipationNotifications(
  notifications: CommandCenterNotification[],
  lookups: LookupState,
  userPreferences: UserPreferences,
  today: string,
  settingsByWorkspace: NotificationSettingsByWorkspace,
  workspaceIdByTeamId: Map<string, string>,
): Promise<void> {
  const teamIds = activeLookupTeamIds(lookups);
  if (teamIds.length === 0) return;
  const { data, error } = await supabase.from('teams').select('id, season_id, seasons(start_date)').eq('is_active', true).is('deleted_at', null).in('id', teamIds);
  if (error) return;
  const activeTeams = ((data || []) as unknown as TeamRow[]).filter((team) => !team.seasons?.start_date || team.seasons.start_date <= today).slice(0, 8);
  await Promise.all(
    activeTeams.map(async (team) => {
      try {
        const { rows } = await loadParticipationReportRows({
          teamId: team.id,
          includeInactivePlayers: false,
          includeSubstitutePlayers: false,
          userPreferences,
          playerLabel: (playerId) => lookupLabel(lookups, 'players', playerId),
        });
        const below: string[] = [];
        const notPlayedRecently: string[] = [];
        const above: string[] = [];
        const belowTarget = getNotificationSetting(settingsByWorkspace, workspaceIdByTeamId.get(team.id), 'players_below_target');
        const notPlayed = getNotificationSetting(settingsByWorkspace, workspaceIdByTeamId.get(team.id), 'player_not_played_recently');
        rows.forEach((row) => {
          if (belowTarget.enabled && row.statusDelta <= -Number(belowTarget.thresholdValue) && below.length < 5) below.push(`${row.playerName} (${row.statusDelta})`);
          if (notPlayed.enabled && row.lastPlayedWeeksAgo !== null && row.lastPlayedWeeksAgo >= Number(notPlayed.thresholdValue) && notPlayedRecently.length < 5) notPlayedRecently.push(`${row.playerName} (${row.lastPlayedWeeksAgo}w)`);
          if (row.statusDelta > 0 && above.length < 5) above.push(`${row.playerName} (+${row.statusDelta})`);
        });
        const teamName = lookupLabel(lookups, 'teams', team.id);
        if (below.length > 0) {
          notifications.push({
            key: `players_below_target:${team.id}`,
            notificationType: 'players_below_target',
            category: 'Alerts & Reminders',
            severity: belowTarget.severity,
            title: 'Players Below Target',
            message: `${teamName} • ${below.join(', ')} • Participation target reminder`,
            teamId: team.id,
            teamName,
            sortDate: today,
            dismissible: true,
            primaryAction: { label: 'Open Participation Report', route: `participation-report:${team.id}` },
          });
        }
        if (notPlayedRecently.length > 0) {
          notifications.push({
            key: `player_not_played_recently:${team.id}`,
            notificationType: 'player_not_played_recently',
            category: 'Alerts & Reminders',
            severity: notPlayed.severity,
            title: "Players Haven't Played Recently",
            message: `${teamName} • ${notPlayedRecently.join(', ')} • No completed match in ${notPlayed.thresholdValue}+ weeks`,
            teamId: team.id,
            teamName,
            sortDate: today,
            dismissible: true,
            primaryAction: { label: 'Open Participation Report', route: `participation-report:${team.id}` },
          });
        }
        if (above.length > 0) {
          notifications.push({
            key: `players_above_target:${team.id}`,
            notificationType: 'players_above_target',
            category: 'Alerts & Reminders',
            severity: 'Informational',
            title: 'Players Above Target',
            message: `${teamName} • ${above.join(', ')} • Participation target reminder`,
            teamId: team.id,
            teamName,
            sortDate: today,
            dismissible: true,
            primaryAction: { label: 'Open Participation Report', route: `participation-report:${team.id}` },
          });
        }
      } catch {
        // One team should not prevent the rest of the Command Center from loading.
      }
    }),
  );
}

async function appendBirthdayNotifications(
  notifications: CommandCenterNotification[],
  lookups: LookupState,
  userPreferences: UserPreferences,
  today: string,
  settingsByWorkspace: NotificationSettingsByWorkspace,
  workspaceIdByTeamId: Map<string, string>,
): Promise<void> {
  const teamIds = activeLookupTeamIds(lookups);
  if (teamIds.length === 0) return;
  const { data, error } = await supabase
    .from('team_rosters')
    .select('player_id, players!inner(id, first_name, last_name, birthday), teams!inner(id, team_name, is_active)')
    .eq('is_active', true)
    .is('deleted_at', null)
    .in('team_id', teamIds)
    .eq('teams.is_active', true)
    .is('teams.deleted_at', null)
    .eq('players.is_active', true)
    .is('players.deleted_at', null);
  if (error) return;
  const now = new Date(`${today}T00:00:00`);
  const currentYear = now.getFullYear();
  const rows = (data || []) as unknown as BirthdayRosterRow[];
  const seen = new Set<string>();
  rows.forEach((row) => {
    const player = row.players;
    if (!player?.birthday || seen.has(player.id)) return;
    const setting = getNotificationSetting(settingsByWorkspace, workspaceIdByTeamId.get(row.teams?.id || ''), 'upcoming_birthday');
    if (!setting.enabled) return;
    seen.add(player.id);
    let occurrence = birthdayOccurrenceThisYear(player.birthday, currentYear);
    if (occurrence < today) occurrence = birthdayOccurrenceThisYear(player.birthday, currentYear + 1);
    if (daysBetween(today, occurrence) > Number(setting.thresholdValue)) return;
    const playerName = formatPlayerName(player, userPreferences) || lookupLabel(lookups, 'players', player.id);
    notifications.push({
      key: `upcoming_birthday:${player.id}:${occurrence}`,
      notificationType: 'upcoming_birthday',
      category: 'Alerts & Reminders',
      severity: setting.severity,
      title: 'Upcoming Birthday',
      message: `${playerName} • ${formatDate(occurrence)} • Player birthday in the next ${setting.thresholdValue} days`,
      playerId: player.id,
      playerName,
      dueDate: occurrence,
      sortDate: occurrence,
      dismissible: true,
      primaryAction: { label: 'Open Player Record', route: 'players' },
    });
  });
}

async function appendRegistrationNotifications(
  notifications: CommandCenterNotification[],
  lookups: LookupState,
  userPreferences: UserPreferences,
  today: string,
  settingsByWorkspace: NotificationSettingsByWorkspace,
  workspaceIdByTeamId: Map<string, string>,
): Promise<void> {
  const teamIds = activeLookupTeamIds(lookups);
  if (teamIds.length === 0) return;
  const { data, error } = await supabase
    .from('team_rosters')
    .select('id, registration_status, players!inner(id, first_name, last_name), teams!inner(id, team_name, is_active, seasons(start_date, end_date))')
    .eq('is_active', true)
    .neq('roster_role', 'designated_substitute')
    .is('deleted_at', null)
    .in('team_id', teamIds)
    .in('registration_status', ['Unknown', 'Not Registered'])
    .eq('teams.is_active', true)
    .is('teams.deleted_at', null)
    .is('players.deleted_at', null)
    .order('created_at', { ascending: true })
    .limit(24);
  if (error) return;
  ((data || []) as unknown as RegistrationRosterRow[]).forEach((row) => {
    const setting = getNotificationSetting(settingsByWorkspace, workspaceIdByTeamId.get(row.teams?.id || ''), 'player_registration_needed');
    if (!setting.enabled) return;
    const season = row.teams?.seasons;
    if (season?.start_date && season.start_date > addDays(today, 21)) return;
    if (season?.end_date && season.end_date < today) return;
    if (!row.players || !row.teams) return;
    const playerName = formatPlayerName(row.players, userPreferences) || lookupLabel(lookups, 'players', row.players.id);
    notifications.push({
      key: `player_registration_needed:${row.id}`,
      notificationType: 'player_registration_needed',
      category: 'Action Required',
      severity: setting.severity,
      title: 'Player Registration Needed',
      message: `${playerName} has not been verified as registered for ${row.teams.team_name}. Registration Status: ${row.registration_status || 'Unknown'}`,
      teamId: row.teams.id,
      teamName: row.teams.team_name,
      playerId: row.players.id,
      playerName,
      rosterId: row.id,
      sortDate: season?.start_date || today,
      dismissible: true,
      primaryAction: { label: 'Mark Registered', kind: 'mark_registered' },
      secondaryAction: { label: 'Open Team Roster', route: `team-roster:${row.teams.id}` },
    });
  });
}
